(() => {
  const languageForms = document.querySelectorAll('[data-language-selector]');
  languageForms.forEach((select) => select.addEventListener('change', () => select.form?.submit()));

  const menuButton = document.querySelector('[data-menu-toggle]');
  const mobileNav = document.querySelector('[data-mobile-nav]');
  if (menuButton && mobileNav) {
    menuButton.addEventListener('click', () => {
      const open = menuButton.getAttribute('aria-expanded') === 'true';
      menuButton.setAttribute('aria-expanded', String(!open));
      mobileNav.hidden = open;
      mobileNav.classList.toggle('is-open', !open);
    });
  }

  const bindDirectionalGesture = (surface, options = {}) => {
    if (!surface || surface.dataset.directionalGestureReady === 'true') return;
    surface.dataset.directionalGestureReady = 'true';

    const ignoreSelector = options.ignoreSelector || 'a, button, input, select, textarea, video, iframe, model-viewer, [contenteditable="true"]';
    const threshold = Math.max(36, Number(options.threshold) || 52);
    let pointer = null;
    let lastWheelNavigation = 0;

    const finishPointer = () => {
      if (!pointer) return;
      try {
        if (surface.hasPointerCapture?.(pointer.id)) surface.releasePointerCapture(pointer.id);
      } catch (_) { /* Pointer capture may already be released. */ }
      pointer = null;
      surface.classList.remove('is-gesture-dragging');
      options.onGestureEnd?.();
    };

    surface.addEventListener('dragstart', (event) => event.preventDefault());
    surface.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
      if (event.target.closest?.(ignoreSelector)) return;
      pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, lastX: event.clientX, lastY: event.clientY };
      try { surface.setPointerCapture?.(event.pointerId); } catch (_) { /* Optional enhancement. */ }
      options.onGestureStart?.();
    });
    surface.addEventListener('pointermove', (event) => {
      if (!pointer || pointer.id !== event.pointerId) return;
      pointer.lastX = event.clientX;
      pointer.lastY = event.clientY;
      const distanceX = event.clientX - pointer.x;
      const distanceY = event.clientY - pointer.y;
      if (Math.abs(distanceX) > 8 && Math.abs(distanceX) > Math.abs(distanceY)) {
        surface.classList.add('is-gesture-dragging');
        if (event.cancelable) event.preventDefault();
      }
    });
    surface.addEventListener('pointerup', (event) => {
      if (!pointer || pointer.id !== event.pointerId) return;
      const distanceX = event.clientX - pointer.x;
      const distanceY = event.clientY - pointer.y;
      const navigate = Math.abs(distanceX) >= threshold && Math.abs(distanceX) > Math.abs(distanceY) * 1.15;
      finishPointer();
      if (navigate) (distanceX < 0 ? options.onNext : options.onPrevious)?.();
    });
    surface.addEventListener('pointercancel', finishPointer);

    surface.addEventListener('wheel', (event) => {
      const horizontalIntent = Math.abs(event.deltaX) >= 22 && Math.abs(event.deltaX) > Math.abs(event.deltaY) * 1.15;
      if (!horizontalIntent) return;
      if (event.cancelable) event.preventDefault();
      const now = Date.now();
      if (now - lastWheelNavigation < 520) return;
      lastWheelNavigation = now;
      options.onGestureStart?.();
      (event.deltaX > 0 ? options.onNext : options.onPrevious)?.();
      options.onGestureEnd?.();
    }, { passive: false });
  };

  const initHeroSlider = (slider) => {
    if (!slider || slider.dataset.heroReady === 'true') return;
    slider.dataset.heroReady = 'true';

    const slides = Array.from(slider.querySelectorAll('[data-hero-slide]'));
    const dots = Array.from(slider.querySelectorAll('[data-hero-dot]'));
    const previous = slider.querySelector('[data-hero-prev]');
    const next = slider.querySelector('[data-hero-next]');
    const currentLabel = slider.querySelector('[data-hero-current]');
    const autoplay = slider.dataset.autoplay === 'true' && !window.Shopify?.designMode;
    const pauseOnHover = slider.dataset.pauseHover === 'true';
    const interval = Math.max(3000, Number(slider.dataset.interval) || 6000);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let timer;

    if (slides.length < 2) return;

    const showSlide = (requestedIndex) => {
      activeIndex = (requestedIndex + slides.length) % slides.length;
      slides.forEach((slide, index) => {
        const active = index === activeIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        if ('inert' in slide) slide.inert = !active;
      });
      dots.forEach((dot, index) => {
        const active = index === activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', String(active));
      });
      if (currentLabel) currentLabel.textContent = String(activeIndex + 1).padStart(2, '0');
    };

    const stop = () => {
      if (timer) window.clearInterval(timer);
      timer = undefined;
    };
    const start = () => {
      stop();
      if (autoplay && !reducedMotion && !document.hidden) {
        timer = window.setInterval(() => showSlide(activeIndex + 1), interval);
      }
    };
    const selectManually = (index) => {
      showSlide(index);
      start();
    };

    previous?.addEventListener('click', () => selectManually(activeIndex - 1));
    next?.addEventListener('click', () => selectManually(activeIndex + 1));
    dots.forEach((dot) => dot.addEventListener('click', () => selectManually(Number(dot.dataset.heroDot))));

    slider.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') selectManually(activeIndex - 1);
      if (event.key === 'ArrowRight') selectManually(activeIndex + 1);
    });
    bindDirectionalGesture(slider, {
      onPrevious: () => selectManually(activeIndex - 1),
      onNext: () => selectManually(activeIndex + 1),
      onGestureStart: stop,
      onGestureEnd: start,
      ignoreSelector: 'a, button, input, select, textarea, video, iframe, model-viewer, [contenteditable="true"]'
    });
    slider.addEventListener('hero:go-to', (event) => selectManually(Number(event.detail?.index || 0)));

    if (pauseOnHover) {
      slider.addEventListener('mouseenter', stop);
      slider.addEventListener('mouseleave', start);
      slider.addEventListener('focusin', stop);
      slider.addEventListener('focusout', (event) => {
        if (!slider.contains(event.relatedTarget)) start();
      });
    }
    document.addEventListener('visibilitychange', () => document.hidden ? stop() : start());

    showSlide(activeIndex);
    start();
  };

  document.querySelectorAll('[data-hero-slider]').forEach(initHeroSlider);
  document.addEventListener('shopify:section:load', (event) => {
    const slider = event.target.querySelector?.('[data-hero-slider]') || event.target.closest?.('[data-hero-slider]');
    initHeroSlider(slider);
  });
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest?.('[data-hero-slide]');
    if (!slide) return;
    slide.closest('[data-hero-slider]')?.dispatchEvent(new CustomEvent('hero:go-to', { detail: { index: Number(slide.dataset.index) } }));
  });

  const initProductMediaGallery = (gallery) => {
    if (!gallery || gallery.dataset.mediaGalleryReady === 'true') return;
    gallery.dataset.mediaGalleryReady = 'true';

    const slides = Array.from(gallery.querySelectorAll('[data-media-slide]'));
    const thumbnails = Array.from(gallery.querySelectorAll('[data-media-thumbnail]'));
    const stage = gallery.querySelector('[data-media-stage]');
    const previous = gallery.querySelector('[data-media-previous]');
    const next = gallery.querySelector('[data-media-next]');
    const current = gallery.querySelector('[data-media-current]');
    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));

    if (!slides.length) return;

    const stopInteractiveMedia = (slide) => {
      slide.querySelectorAll('video').forEach((video) => video.pause());
      slide.querySelectorAll('model-viewer').forEach((model) => model.pause?.());
      slide.querySelectorAll('iframe').forEach((frame) => {
        if (frame.dataset.gallerySource) return;
        frame.dataset.gallerySource = frame.src;
      });
    };

    const stopExternalVideo = (slide) => {
      slide.querySelectorAll('iframe').forEach((frame) => {
        const source = frame.dataset.gallerySource || frame.src;
        if (!source) return;
        frame.dataset.gallerySource = source;
        frame.src = source;
      });
    };

    const showMedia = (requestedIndex, moveFocus = false) => {
      activeIndex = (Number(requestedIndex) + slides.length) % slides.length;
      slides.forEach((slide, index) => {
        const active = index === activeIndex;
        if (!active && !slide.hidden) {
          stopInteractiveMedia(slide);
          stopExternalVideo(slide);
        }
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      thumbnails.forEach((thumbnail, index) => {
        const active = index === activeIndex;
        thumbnail.classList.toggle('is-active', active);
        thumbnail.setAttribute('aria-selected', String(active));
        thumbnail.tabIndex = active ? 0 : -1;
      });
      const selectedThumbnail = thumbnails[activeIndex];
      selectedThumbnail?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      if (current) current.textContent = String(activeIndex + 1);
      if (moveFocus) selectedThumbnail?.focus({ preventScroll: true });
    };

    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => showMedia(index));
      thumbnail.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown' && event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        showMedia(activeIndex + ((event.key === 'ArrowDown' || event.key === 'ArrowRight') ? 1 : -1), true);
      });
    });
    previous?.addEventListener('click', () => showMedia(activeIndex - 1));
    next?.addEventListener('click', () => showMedia(activeIndex + 1));

    bindDirectionalGesture(stage, {
      onPrevious: () => showMedia(activeIndex - 1),
      onNext: () => showMedia(activeIndex + 1),
      ignoreSelector: 'button, video, iframe, model-viewer, [contenteditable="true"]'
    });

    gallery.addEventListener('product-media:select', (event) => {
      const requestedId = String(event.detail?.id || '');
      const requestedIndex = slides.findIndex((slide) => String(slide.dataset.mediaSlide) === requestedId);
      if (requestedIndex >= 0) showMedia(requestedIndex);
    });

    showMedia(activeIndex);
  };

  document.querySelectorAll('[data-product-media-gallery]').forEach(initProductMediaGallery);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('[data-product-media-gallery]').forEach(initProductMediaGallery);
  });

  let activeProductCardPreview = null;
  const stopProductCardPreview = (media) => {
    if (!media) return;
    media.querySelector('video')?.pause();
    const video = media.querySelector('video');
    if (video) {
      try { video.currentTime = 0; } catch (_) { /* Some streams cannot seek before loading. */ }
    }
    media.querySelector('[data-product-card-external-host]')?.replaceChildren();
    media.classList.remove('is-previewing', 'is-video-ready');
    if (activeProductCardPreview === media) activeProductCardPreview = null;
  };

  const startProductCardPreview = async (media) => {
    if (!media || media.classList.contains('is-previewing')) return;
    if (activeProductCardPreview && activeProductCardPreview !== media) stopProductCardPreview(activeProductCardPreview);
    activeProductCardPreview = media;
    media.classList.add('is-previewing');

    if (media.dataset.previewType === 'external_video') {
      const host = media.querySelector('[data-product-card-external-host]');
      const template = media.querySelector('[data-product-card-external-template]');
      if (!host || !template) return stopProductCardPreview(media);
      const fragment = template.content.cloneNode(true);
      const frame = fragment.querySelector('iframe');
      if (!frame) return stopProductCardPreview(media);
      try {
        const source = new URL(frame.src, window.location.href);
        source.searchParams.set('autoplay', '1');
        source.searchParams.set('mute', '1');
        source.searchParams.set('muted', '1');
        source.searchParams.set('playsinline', '1');
        source.searchParams.set('controls', '0');
        source.searchParams.set('background', '1');
        frame.src = source.toString();
      } catch (_) { /* Keep the Shopify-generated URL when it cannot be parsed. */ }
      frame.tabIndex = -1;
      host.append(fragment);
      media.classList.add('is-video-ready');
      return;
    }

    const video = media.querySelector('video');
    if (!video) return stopProductCardPreview(media);
    video.muted = true;
    video.playsInline = true;
    try {
      video.currentTime = 0;
      await video.play();
      if (activeProductCardPreview === media) {
        media.classList.add('is-video-ready');
      } else {
        video.pause();
        try { video.currentTime = 0; } catch (_) { /* Some streams cannot seek before loading. */ }
      }
    } catch (_) {
      stopProductCardPreview(media);
    }
  };

  const initProductCardVideoPreview = (media) => {
    if (!media || media.dataset.cardVideoReady === 'true') return;
    media.dataset.cardVideoReady = 'true';
    const holdDelay = Math.max(300, Number(media.dataset.previewHoldDelay) || 400);
    let holdTimer;
    let touchPointer = null;
    let suppressClick = false;
    let suppressResetTimer;

    const cancelHold = () => {
      window.clearTimeout(holdTimer);
      holdTimer = undefined;
    };
    const finishTouch = (allowClickReset = true) => {
      cancelHold();
      if (activeProductCardPreview === media) stopProductCardPreview(media);
      touchPointer = null;
      window.clearTimeout(suppressResetTimer);
      if (allowClickReset && suppressClick) suppressResetTimer = window.setTimeout(() => { suppressClick = false; }, 250);
    };

    media.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') startProductCardPreview(media);
    });
    media.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse') stopProductCardPreview(media);
      if (touchPointer) finishTouch(true);
    });
    media.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' || !event.isPrimary) return;
      touchPointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
      holdTimer = window.setTimeout(() => {
        if (!touchPointer) return;
        suppressClick = true;
        startProductCardPreview(media);
      }, holdDelay);
    });
    media.addEventListener('pointermove', (event) => {
      if (!touchPointer || touchPointer.id !== event.pointerId) return;
      if (Math.hypot(event.clientX - touchPointer.x, event.clientY - touchPointer.y) <= 12) return;
      finishTouch(false);
      suppressClick = false;
    });
    media.addEventListener('pointerup', (event) => {
      if (!touchPointer || touchPointer.id !== event.pointerId) return;
      finishTouch(true);
    });
    media.addEventListener('pointercancel', () => {
      finishTouch(false);
      suppressClick = false;
    });
    media.addEventListener('contextmenu', (event) => {
      if (touchPointer || suppressClick) event.preventDefault();
    });
    media.addEventListener('click', (event) => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      suppressClick = false;
      window.clearTimeout(suppressResetTimer);
    });
  };

  document.querySelectorAll('[data-product-card-video]').forEach(initProductCardVideoPreview);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('[data-product-card-video]').forEach(initProductCardVideoPreview);
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stopProductCardPreview(activeProductCardPreview);
  });

  const initCampaignStrip = (campaign) => {
    if (!campaign || campaign.dataset.campaignReady === 'true') return;
    campaign.dataset.campaignReady = 'true';

    const campaignKey = `lumiere-campaign-dismissed:${campaign.dataset.campaignKey || campaign.id}`;
    const countdown = campaign.querySelector('[data-campaign-countdown]');
    const endedMessage = campaign.querySelector('[data-campaign-ended]');
    const endTimestamp = Date.parse(campaign.dataset.campaignEnd || '');
    const hideAfterEnd = campaign.dataset.hideAfterEnd === 'true';
    let timer;

    if (!window.Shopify?.designMode) {
      try {
        if (window.sessionStorage.getItem(campaignKey) === 'true') campaign.hidden = true;
      } catch (_) { /* Session storage can be unavailable. */ }
    }

    const finishCampaign = () => {
      window.clearInterval(timer);
      if (hideAfterEnd && !window.Shopify?.designMode) {
        campaign.hidden = true;
        return;
      }
      countdown?.setAttribute('hidden', '');
      if (endedMessage) endedMessage.hidden = false;
    };

    const updateCountdown = () => {
      if (!countdown || !Number.isFinite(endTimestamp)) {
        countdown?.setAttribute('hidden', '');
        return;
      }
      const remaining = endTimestamp - Date.now();
      if (remaining <= 0) {
        finishCampaign();
        return;
      }
      const totalSeconds = Math.floor(remaining / 1000);
      const values = {
        days: Math.floor(totalSeconds / 86400),
        hours: Math.floor((totalSeconds % 86400) / 3600),
        minutes: Math.floor((totalSeconds % 3600) / 60),
        seconds: totalSeconds % 60
      };
      Object.entries(values).forEach(([key, value]) => {
        const target = countdown.querySelector(`[data-campaign-${key}]`);
        if (target) target.textContent = String(value).padStart(2, '0');
      });
    };

    const codeButton = campaign.querySelector('[data-campaign-code]');
    codeButton?.addEventListener('click', async () => {
      const code = codeButton.dataset.campaignCode || '';
      if (!code) return;
      try {
        await navigator.clipboard.writeText(code);
        const status = codeButton.querySelector('[data-campaign-copy-status]');
        if (status) {
          const previousText = status.textContent;
          status.textContent = codeButton.closest('[data-campaign-strip]')?.dataset.copiedLabel || 'Copied';
          window.setTimeout(() => { status.textContent = previousText; }, 1800);
        }
      } catch (_) { /* The code remains visible for manual copy. */ }
    });

    campaign.querySelector('[data-campaign-dismiss]')?.addEventListener('click', () => {
      campaign.hidden = true;
      try { window.sessionStorage.setItem(campaignKey, 'true'); } catch (_) { /* Ignore storage failures. */ }
    });

    if (countdown && Number.isFinite(endTimestamp)) {
      updateCountdown();
      timer = window.setInterval(updateCountdown, 1000);
      campaign._campaignTimer = timer;
    } else {
      countdown?.setAttribute('hidden', '');
    }
  };

  document.querySelectorAll('[data-campaign-strip]').forEach(initCampaignStrip);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('[data-campaign-strip]').forEach(initCampaignStrip);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-campaign-strip]').forEach((campaign) => window.clearInterval(campaign._campaignTimer));
  });

  document.querySelectorAll('[data-quantity-change]').forEach((button) => {
    button.addEventListener('click', () => {
      const input = button.parentElement?.querySelector('[data-quantity-input]');
      if (!input) return;
      input.value = String(Math.max(1, Number(input.value || 1) + Number(button.dataset.quantityChange)));
    });
  });

  const initProductForm = (form) => {
    if (!form || form.dataset.productFormReady === 'true') return;
    form.dataset.productFormReady = 'true';

    const section = form.closest('[data-product-section]');
    const optionSelectors = Array.from(form.querySelectorAll('[data-product-option]'));
    const variantInput = form.querySelector('[data-variant-id]');
    const variantsData = form.querySelector('[data-product-variants]');
    const price = section?.querySelector('[data-product-price]');
    const submit = form.querySelector('[data-add-to-cart]');
    const personalization = form.querySelector('[data-personalization-field]');
    const engravingInput = form.querySelector('[data-personalization-input]');
    const engravingCount = form.querySelector('[data-engraving-count]');
    let variants = [];

    if (variantsData) {
      try { variants = JSON.parse(variantsData.textContent || '[]'); } catch (_) { variants = []; }
    }

    const normalize = (value = '') => String(value).trim().toLocaleLowerCase();
    const selectedValues = () => optionSelectors.map((select) => select.value);

    const updatePersonalization = (values) => {
      if (!personalization || !engravingInput) return;
      const triggerValue = normalize(personalization.dataset.personalizedValue);
      const optionName = normalize(personalization.dataset.personalizationOptionName);
      const triggerSelect = optionSelectors.find((select) => normalize(select.dataset.optionName) === optionName);
      const active = Boolean(triggerValue) && (triggerSelect
        ? normalize(triggerSelect.value) === triggerValue
        : values.some((value) => normalize(value) === triggerValue));
      personalization.hidden = !active;
      personalization.setAttribute('aria-hidden', String(!active));
      engravingInput.disabled = !active;
      engravingInput.required = active;
      if (!active) engravingInput.setCustomValidity('');
    };

    const updateVariant = () => {
      const values = selectedValues();
      const variant = variants.find((item) => item.options?.every((value, index) => value === values[index]));
      updatePersonalization(values);

      if (!variant || !variantInput || !submit) {
        if (variantInput) variantInput.value = '';
        if (submit) {
          submit.disabled = true;
          submit.textContent = submit.dataset.unavailableLabel || 'Unavailable';
        }
        return;
      }

      variantInput.value = String(variant.id);
      submit.disabled = !variant.available;
      submit.textContent = variant.available
        ? (submit.dataset.addLabel || 'Add to cart')
        : (submit.dataset.soldOutLabel || 'Sold out');
      if (price) {
        const discount = Number(variant.discount) || 0;
        price.classList.toggle('is-sale', discount > 0);
        price.innerHTML = `<strong>${variant.price || ''}</strong>${variant.compare ? `<del>${variant.compare}</del><span class="product-price__saving">${discount}% ${price.dataset.offLabel || 'OFF'}</span>` : ''}`;
      }
      if (variant.media_id) {
        section?.querySelector('[data-product-media-gallery]')?.dispatchEvent(new CustomEvent('product-media:select', { detail: { id: variant.media_id } }));
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };

    optionSelectors.forEach((select) => select.addEventListener('change', updateVariant));
    engravingInput?.addEventListener('input', () => {
      if (engravingCount) engravingCount.textContent = String(engravingInput.value.length);
      if (engravingInput.value.trim()) engravingInput.setCustomValidity('');
    });
    form.addEventListener('submit', (event) => {
      if (!engravingInput || engravingInput.disabled) return;
      const engraving = engravingInput.value.trim();
      if (!engraving) {
        event.preventDefault();
        engravingInput.setCustomValidity(personalization?.dataset.requiredMessage || 'Please enter engraving text.');
        engravingInput.reportValidity();
        engravingInput.focus();
        return;
      }
      engravingInput.value = engraving;
    });

    if (engravingCount && engravingInput) engravingCount.textContent = String(engravingInput.value.length);
    if (optionSelectors.length) updateVariant();
  };

  document.querySelectorAll('[data-product-section] .product-form').forEach(initProductForm);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('[data-product-section] .product-form, .product-form').forEach(initProductForm);
  });

  document.querySelectorAll('[data-sort-select]').forEach((select) => {
    select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.assign(url.toString());
    });
  });

  document.querySelectorAll('[data-header-details]').forEach((details) => {
    document.addEventListener('click', (event) => {
      if (details.open && !details.contains(event.target)) details.open = false;
    });
  });

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  })[character]);

  const normalizeSearchText = (value = '') => String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'd')
    .toLocaleLowerCase()
    .replace(/[-_/]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const initSmartSearch = (form) => {
    if (!form || form.dataset.searchReady === 'true') return;
    const input = form.querySelector('[data-header-search-input]');
    const panel = form.querySelector('[data-predictive-results]');
    const content = form.querySelector('[data-search-panel-content]');
    const clearButton = form.querySelector('[data-search-clear]');
    const categoryPicker = form.querySelector('[data-header-details]');
    const discoveryTemplate = document.getElementById(form.dataset.searchTemplate || '');
    if (!input || !panel || !content) return;

    form.dataset.searchReady = 'true';
    const predictiveEnabled = form.dataset.predictiveEnabled !== 'false';
    const historyEnabled = form.dataset.searchHistory !== 'false';
    const minCharacters = Math.max(1, Number(form.dataset.searchMinChars) || 1);
    const historyKey = `lumiere-search-history:${document.documentElement.lang || 'default'}`;
    const responseCache = new Map();
    let searchTimer;
    let controller;
    let activeIndex = -1;

    const readHistory = () => {
      if (!historyEnabled) return [];
      try {
        const value = JSON.parse(window.localStorage.getItem(historyKey) || '[]');
        return Array.isArray(value) ? value.filter((item) => typeof item === 'string' && item.trim()).slice(0, 6) : [];
      } catch (_) {
        return [];
      }
    };

    const writeHistory = (items) => {
      if (!historyEnabled) return;
      try { window.localStorage.setItem(historyKey, JSON.stringify(items.slice(0, 6))); } catch (_) { /* Storage can be unavailable in private mode. */ }
    };

    const saveHistory = (term) => {
      const cleanTerm = String(term || '').trim();
      if (!cleanTerm) return;
      const items = readHistory().filter((item) => item.toLocaleLowerCase() !== cleanTerm.toLocaleLowerCase());
      writeHistory([cleanTerm, ...items]);
    };

    const setOpen = (open) => {
      panel.hidden = !open;
      input.setAttribute('aria-expanded', String(open));
      document.body.classList.toggle('search-experience-open', open);
      if (!open) activeIndex = -1;
    };

    const updateClearButton = () => {
      if (clearButton) clearButton.hidden = input.value.length === 0;
    };

    const getOptions = () => Array.from(content.querySelectorAll('[data-search-option]')).filter((option) => !option.hidden && !option.disabled);
    const setActiveOption = (requestedIndex) => {
      const options = getOptions();
      if (!options.length) return;
      activeIndex = (requestedIndex + options.length) % options.length;
      options.forEach((option, index) => {
        const active = index === activeIndex;
        option.classList.toggle('is-active', active);
        option.setAttribute('aria-selected', String(active));
      });
      options[activeIndex]?.scrollIntoView({ block: 'nearest' });
    };

    const renderHistory = () => {
      const section = content.querySelector('[data-search-history-section]');
      const list = content.querySelector('[data-search-history-list]');
      if (!section || !list) return;
      const items = readHistory();
      section.hidden = items.length === 0;
      list.innerHTML = items.map((item) => `
        <span class="search-history-chip">
          <button type="button" data-search-option data-search-submit-term="${escapeHtml(item)}"><span aria-hidden="true">↺</span><span>${escapeHtml(item)}</span></button>
          <button type="button" data-search-history-remove="${escapeHtml(item)}" aria-label="${escapeHtml(form.dataset.searchRemoveLabel || 'Remove')} ${escapeHtml(item)}">×</button>
        </span>`).join('');
    };

    const renderDiscovery = () => {
      controller?.abort();
      window.clearTimeout(searchTimer);
      if (!discoveryTemplate) {
        setOpen(false);
        return;
      }
      content.replaceChildren(discoveryTemplate.content.cloneNode(true));
      renderHistory();
      activeIndex = -1;
      setOpen(true);
    };

    const renderLoading = () => {
      content.innerHTML = `<div class="predictive-search__loading"><span aria-hidden="true"></span><strong>${escapeHtml(form.dataset.searchLoadingLabel || 'Searching...')}</strong><i></i><i></i><i></i></div>`;
      activeIndex = -1;
      setOpen(true);
    };

    const applyResponse = (html, term) => {
      if (input.value.trim() !== term) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const section = parsed.querySelector('#shopify-section-predictive-search');
      const markup = section?.innerHTML.trim();
      if (!markup) {
        setOpen(false);
        return;
      }
      content.innerHTML = markup;
      activeIndex = -1;
      setOpen(true);
    };

    const requestSuggestions = async (term) => {
      if (responseCache.has(term)) {
        applyResponse(responseCache.get(term), term);
        return;
      }
      controller?.abort();
      controller = new AbortController();
      renderLoading();
      try {
        const root = window.Shopify?.routes?.root || '/';
        const endpointPath = form.dataset.searchEndpoint || `${root}search/suggest`;
        const endpoint = new URL(endpointPath, window.location.origin);
        endpoint.searchParams.set('q', term);
        endpoint.searchParams.set('resources[type]', 'query,product,collection');
        endpoint.searchParams.set('resources[limit]', '6');
        endpoint.searchParams.set('resources[limit_scope]', 'each');
        endpoint.searchParams.set('resources[options][unavailable_products]', 'last');
        endpoint.searchParams.set('resources[options][fields]', form.dataset.searchFields || 'title,product_type,variants.title,vendor');
        endpoint.searchParams.set('section_id', 'predictive-search');
        const response = await fetch(endpoint.toString(), { signal: controller.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
        const html = await response.text();
        responseCache.set(term, html);
        if (responseCache.size > 20) responseCache.delete(responseCache.keys().next().value);
        applyResponse(html, term);
      } catch (error) {
        if (error.name !== 'AbortError') setOpen(false);
      }
    };

    const handleInput = () => {
      window.clearTimeout(searchTimer);
      updateClearButton();
      activeIndex = -1;
      if (categoryPicker) categoryPicker.open = false;
      const term = input.value.trim();
      if (!term || term.length < minCharacters || !predictiveEnabled) {
        renderDiscovery();
        return;
      }
      searchTimer = window.setTimeout(() => requestSuggestions(term), 200);
    };

    input.addEventListener('focus', () => {
      const term = input.value.trim();
      if (term.length >= minCharacters && predictiveEnabled) requestSuggestions(term);
      else renderDiscovery();
    });
    input.addEventListener('input', handleInput);
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        return;
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (panel.hidden) renderDiscovery();
        setActiveOption(activeIndex + (event.key === 'ArrowDown' ? 1 : -1));
        return;
      }
      if (event.key === 'Enter' && activeIndex >= 0) {
        const option = getOptions()[activeIndex];
        if (option) {
          event.preventDefault();
          option.click();
        }
      }
    });

    clearButton?.addEventListener('click', () => {
      input.value = '';
      updateClearButton();
      renderDiscovery();
      input.focus();
    });
    form.querySelector('[data-search-close]')?.addEventListener('click', () => setOpen(false));
    categoryPicker?.addEventListener('toggle', () => {
      if (categoryPicker.open) setOpen(false);
    });

    content.addEventListener('click', (event) => {
      const clearHistory = event.target.closest('[data-search-history-clear]');
      if (clearHistory) {
        writeHistory([]);
        renderDiscovery();
        return;
      }
      const removeHistory = event.target.closest('[data-search-history-remove]');
      if (removeHistory) {
        const removeTerm = removeHistory.dataset.searchHistoryRemove || '';
        writeHistory(readHistory().filter((item) => item !== removeTerm));
        renderDiscovery();
        return;
      }
      const submitTerm = event.target.closest('[data-search-submit-term]');
      if (submitTerm) {
        input.value = submitTerm.dataset.searchSubmitTerm || '';
        saveHistory(input.value);
        form.requestSubmit();
        return;
      }
      const selectedOption = event.target.closest('[data-search-option]');
      if (selectedOption) saveHistory(selectedOption.dataset.searchTerm || input.value);
    });

    form.addEventListener('submit', (event) => {
      const term = input.value.trim();
      if (!term) {
        event.preventDefault();
        renderDiscovery();
        input.focus();
        return;
      }
      saveHistory(term);
    });
    document.addEventListener('click', (event) => {
      if (!form.contains(event.target)) setOpen(false);
    });

    updateClearButton();
  };

  document.querySelectorAll('[data-header-search]').forEach(initSmartSearch);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('[data-header-search]').forEach(initSmartSearch);
  });

  const initExpandedCatalogSearch = (root) => {
    if (!root || root.dataset.expandedSearchReady === 'true' || Number(root.dataset.nativeResults) > 0) return;
    const catalog = root.querySelector('[data-smart-search-catalog]');
    const grid = root.querySelector('[data-smart-search-grid]');
    const empty = root.querySelector('[data-smart-search-empty]');
    const count = root.querySelector('[data-search-result-count]');
    const matchMode = root.querySelector('[data-smart-search-mode]');
    const normalizedQuery = normalizeSearchText(root.dataset.searchQuery || '');
    if (!catalog || !grid || !normalizedQuery) return;

    root.dataset.expandedSearchReady = 'true';
    const queryTerms = normalizedQuery.split(' ').filter(Boolean);
    const matches = Array.from(catalog.querySelectorAll('[data-smart-search-product]')).filter((item) => {
      const searchableText = normalizeSearchText(item.dataset.searchText || '');
      return queryTerms.every((term) => searchableText.includes(term));
    });

    const sortBy = new URL(window.location.href).searchParams.get('sort_by') || 'relevance';
    const titleCollator = new Intl.Collator(document.documentElement.lang || undefined, { sensitivity: 'base', numeric: true });
    const numericValue = (item, key) => Number(item.dataset[key] || 0);
    if (sortBy === 'price-ascending') matches.sort((a, b) => numericValue(a, 'searchPrice') - numericValue(b, 'searchPrice'));
    if (sortBy === 'price-descending') matches.sort((a, b) => numericValue(b, 'searchPrice') - numericValue(a, 'searchPrice'));
    if (sortBy === 'created-ascending') matches.sort((a, b) => numericValue(a, 'searchDate') - numericValue(b, 'searchDate'));
    if (sortBy === 'created-descending') matches.sort((a, b) => numericValue(b, 'searchDate') - numericValue(a, 'searchDate'));
    if (sortBy === 'title-ascending') matches.sort((a, b) => titleCollator.compare(a.dataset.searchTitle || '', b.dataset.searchTitle || ''));
    if (sortBy === 'title-descending') matches.sort((a, b) => titleCollator.compare(b.dataset.searchTitle || '', a.dataset.searchTitle || ''));

    if (matches.length > 0) {
      matches.forEach((item) => {
        const productCard = item.querySelector('.product-card');
        if (productCard) grid.append(productCard);
      });
      grid.hidden = false;
      if (empty) empty.hidden = true;
      if (matchMode) matchMode.hidden = false;
      const countTemplate = root.dataset.searchCountTemplate || '[[count]] products found';
      if (count) count.textContent = countTemplate.replace('[[count]]', String(matches.length));
      root.classList.add('smart-search-expanded');
    }
    catalog.remove();
  };

  document.querySelectorAll('[data-smart-search-root]').forEach(initExpandedCatalogSearch);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('[data-smart-search-root]').forEach(initExpandedCatalogSearch);
  });

  const filterSidebar = document.querySelector('[data-filter-sidebar]');
  const filterOverlay = document.querySelector('.shop-filter-overlay');
  const setFilterDrawer = (open) => {
    filterSidebar?.classList.toggle('is-open', open);
    if (filterOverlay) filterOverlay.hidden = !open;
    document.body.classList.toggle('filter-drawer-open', open);
  };
  document.querySelector('[data-filter-open]')?.addEventListener('click', () => setFilterDrawer(true));
  document.querySelectorAll('[data-filter-close]').forEach((button) => button.addEventListener('click', () => setFilterDrawer(false)));

  document.querySelectorAll('[data-grid-columns]').forEach((button) => {
    button.addEventListener('click', () => {
      const grid = document.querySelector('[data-shop-grid]');
      if (!grid) return;
      const fourColumns = button.dataset.gridColumns === '4';
      grid.classList.toggle('is-four-columns', fourColumns);
      button.parentElement?.querySelectorAll('[data-grid-columns]').forEach((item) => item.classList.toggle('is-active', item === button));
    });
  });

  document.querySelectorAll('[data-faq-filter]').forEach((button) => {
    button.addEventListener('click', () => {
      const page = button.closest('[data-faq-page]');
      const category = button.dataset.faqFilter;
      page?.querySelectorAll('[data-faq-filter]').forEach((item) => item.classList.toggle('is-active', item === button));
      page?.querySelectorAll('[data-faq-category]').forEach((item) => {
        const visible = category === 'all' || item.dataset.faqCategory === category;
        item.hidden = !visible;
        if (!visible) item.open = false;
      });
    });
  });

  const wishlistKey = 'lumiere-resin-wishlist';
  const readWishlist = () => {
    try {
      const value = JSON.parse(window.localStorage.getItem(wishlistKey) || '[]');
      return Array.isArray(value) ? value.filter(Boolean) : [];
    } catch { return []; }
  };
  const writeWishlist = (handles) => window.localStorage.setItem(wishlistKey, JSON.stringify([...new Set(handles)]));
  const updateWishlistUI = () => {
    const handles = readWishlist();
    document.querySelectorAll('[data-wishlist-count]').forEach((counter) => {
      counter.textContent = String(handles.length);
      counter.hidden = handles.length === 0;
    });
    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => button.classList.toggle('is-active', handles.includes(button.dataset.productHandle)));
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;
    event.preventDefault();
    const handle = button.dataset.productHandle;
    if (!handle) return;
    const handles = readWishlist();
    const next = handles.includes(handle) ? handles.filter((item) => item !== handle) : [...handles, handle];
    writeWishlist(next);
    Array.from(document.querySelectorAll('[data-wishlist-item]')).find((item) => item.dataset.wishlistItem === handle)?.remove();
    updateWishlistUI();
    if (!next.length) document.querySelector('[data-wishlist-empty]')?.removeAttribute('hidden');
  });

  const wishlistGrid = document.querySelector('[data-wishlist-grid]');
  if (wishlistGrid) {
    const handles = readWishlist();
    const empty = document.querySelector('[data-wishlist-empty]');
    const currency = wishlistGrid.dataset.currency || 'USD';
    const removeLabel = wishlistGrid.dataset.removeLabel || 'Remove';
    const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency });
    if (!handles.length) {
      empty?.removeAttribute('hidden');
    } else {
      Promise.all(handles.map(async (handle) => {
        try {
          const root = wishlistGrid.dataset.rootUrl || '/';
          const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`);
          return response.ok ? response.json() : null;
        } catch { return null; }
      })).then((products) => {
        const availableProducts = products.filter(Boolean);
        if (!availableProducts.length) {
          empty?.removeAttribute('hidden');
          return;
        }
        wishlistGrid.innerHTML = availableProducts.map((product) => `
          <article class="product-card" data-wishlist-item="${escapeHtml(product.handle)}">
            <a class="product-card__media" href="${escapeHtml(product.url)}">${product.featured_image ? `<img src="${escapeHtml(product.featured_image)}" alt="${escapeHtml(product.title)}" loading="lazy">` : ''}</a>
            <button class="wishlist-button is-active" type="button" data-wishlist-toggle data-product-handle="${escapeHtml(product.handle)}" aria-label="${escapeHtml(removeLabel)} ${escapeHtml(product.title)}">♡</button>
            <div class="product-card__info"><small>${escapeHtml(product.type || 'Epoxy Resin')}</small><h3><a href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a></h3><div class="product-card__bottom"><p>${formatter.format((product.price || 0) / 100)}</p><a class="circle-button" href="${escapeHtml(product.url)}">→</a></div></div>
          </article>`).join('');
        updateWishlistUI();
      });
    }
  }

  updateWishlistUI();
})();
